/**
 * Утилита для обработки ошибок модерации (мута) на фронтенде.
 *
 * Сервер возвращает 400 BAD_REQUEST с сообщениями:
 * — "You are muted and cannot send messages" (для чатов)
 * — "You are muted and cannot post on the forum" (для форума)
 */

const MUTED_CHAT_PATTERNS = [
    /muted.*cannot.*send/i,
    /cannot.*send.*muted/i,
    /you are muted/i,
];

const MUTED_FORUM_PATTERNS = [
    /muted.*cannot.*post/i,
    /cannot.*post.*muted/i,
    /muted.*forum/i,
];

/**
 * Проверяет, является ли ошибка ошибкой мута (для чатов)
 * @param {Error|Object} err — объект ошибки axios
 * @returns {boolean}
 */
export const isMutedChatError = (err) => {
    const message = err?.response?.data?.message || err?.message || '';
    return MUTED_CHAT_PATTERNS.some(p => p.test(message));
};

/**
 * Проверяет, является ли ошибка ошибкой мута (для форума)
 * @param {Error|Object} err — объект ошибки axios
 * @returns {boolean}
 */
export const isMutedForumError = (err) => {
    const message = err?.response?.data?.message || err?.message || '';
    return MUTED_FORUM_PATTERNS.some(p => p.test(message));
};

/**
 * Проверяет, является ли ошибка ошибкой мута (любого типа)
 * @param {Error|Object} err — объект ошибки axios
 * @returns {boolean}
 */
export const isMutedError = (err) => {
    return isMutedChatError(err) || isMutedForumError(err);
};

/**
 * Возвращает пользовательское сообщение об ошибке мута
 * @param {Error|Object} err — объект ошибки axios
 * @returns {string|null} — сообщение или null, если это не ошибка мута
 */
export const getMutedErrorMessage = (err) => {
    if (isMutedForumError(err)) {
        return 'Вы не можете писать на форуме — на вас наложен мут (жёлтая карточка).';
    }
    if (isMutedChatError(err)) {
        return 'Вы не можете отправлять сообщения — на вас наложен мут (жёлтая карточка).';
    }
    return null;
};

/**
 * Проверяет, является ли ошибка блокировкой IP/HWID (403 от AuthController)
 * @param {Error|Object} err — объект ошибки axios
 * @returns {boolean}
 */
export const isBlockedError = (err) => {
    if (err?.response?.status !== 403) return false;
    const message = err?.response?.data?.message || err?.message || '';
    return /blocked/i.test(message) || /ip.*block/i.test(message) || /hwid.*block/i.test(message);
};

/**
 * Возвращает пользовательское сообщение о блокировке IP/HWID
 * @param {Error|Object} err — объект ошибки axios
 * @returns {string|null} — сообщение или null, если это не блокировка
 */
export const getBlockedErrorMessage = (err) => {
    if (!isBlockedError(err)) return null;
    return 'Ваш IP-адрес или устройство заблокированы за нарушение правил платформы. Обратитесь к администрации для подачи апелляции.';
};

/**
 * Проверяет, является ли ошибка ошибкой доступа (403 — не автор контента)
 * @param {Error|Object} err — объект ошибки axios
 * @returns {boolean}
 */
export const isAccessDeniedError = (err) => {
    if (err?.response?.status !== 403) return false;
    // Исключаем блокировки IP/HWID — это отдельный случай
    if (isBlockedError(err)) return false;
    return true;
};

/**
 * Возвращает пользовательское сообщение об ошибке доступа
 * @param {Error|Object} err — объект ошибки axios
 * @returns {string|null} — сообщение или null
 */
export const getAccessDeniedMessage = (err) => {
    if (!isAccessDeniedError(err)) return null;
    const serverMsg = err?.response?.data?.message || '';
    if (serverMsg) return serverMsg;
    return 'У вас нет прав для выполнения этого действия. Вы можете редактировать и удалять только свои посты и комментарии.';
};
